import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppBar, Box, Button, CircularProgress, Grid, Paper, Toolbar, Typography } from "@mui/material";
import { Save } from "@mui/icons-material";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import SortableSessionList from "./SortableSessionList";
import SessionAddPicker from "./SessionAddPicker";

/**
 * Generic page that manages an ordered list of sessions backed by a single
 * GET/PUT endpoint pair (recommended-list or popular-list). The slice name
 * (`recommended_list` or `popular_list`) and the corresponding thunks are
 * supplied by the parent.
 */
const EngagementSessionListPage = ({ title, subtitle, description, sliceName, selectList, fetchList, replaceList }) => {
  const dispatch = useDispatch();
  const listState = useSelector(state => (selectList ? selectList(state) : state[sliceName]));
  const items = listState?.items;
  const isLoading = listState?.isLoading;
  // A per-topic list (selectList) is editable only once it has loaded successfully.
  const editable = selectList ? !!listState?.loaded && !listState?.hasError : true;

  // Local working copy. Stays in sync with redux on fetch but allows the admin
  // to reorder / add / remove before committing via "Save changes".
  const [workingList, setWorkingList] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    Promise.resolve(dispatch(fetchList())).catch(() => {});
  }, [dispatch, fetchList]);

  useEffect(() => {
    if (Array.isArray(items) && (!selectList || listState?.loaded)) {
      setWorkingList(items);
      setIsDirty(false);
    } else if (selectList) {
      setWorkingList([]);
      setIsDirty(false);
    }
  }, [items, selectList, listState?.loaded]);

  const excludeIds = useMemo(() => workingList.map(s => s.session_id), [workingList]);

  const handleReorder = (oldIndex, newIndex) => {
    if (!editable) return;
    setWorkingList(prev => {
      const next = [...prev];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      return next;
    });
    setIsDirty(true);
  };

  const handleRemove = sessionId => {
    if (!editable) return;
    setWorkingList(prev => prev.filter(s => s.session_id !== sessionId));
    setIsDirty(true);
  };

  const handleAdd = session => {
    if (!editable) return;
    // Defensive guard — picker already excludes via excludeIds, so a stale
    // closure here is essentially impossible in practice.
    if (workingList.some(s => s.session_id === session.session_id)) return;
    setWorkingList(prev => [...prev, session]);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!editable) return;
    try {
      setIsSaving(true);
      const sessionIds = workingList.map(s => s.session_id);
      await dispatch(replaceList(sessionIds));
      setIsDirty(false);
    } catch (err) {
      // errorHandler already shows a Swal — nothing else to do here
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (Array.isArray(items)) {
      setWorkingList(items);
      setIsDirty(false);
    }
  };

  return (
    <MainContentContainer sx={{ paddingBottom: 4 }}>
      <AppBar position="sticky">
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.2 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {isDirty && (
            <Button color="inherit" onClick={handleDiscard} disabled={isSaving} sx={{ mr: 2 }}>
              Discard changes
            </Button>
          )}
          <Button
            color="success"
            onClick={handleSave}
            startIcon={<Save />}
            variant="contained"
            disabled={!editable || !isDirty || isSaving}
          >
            {isSaving ? "Saving…" : "Save changes"}
          </Button>
        </Toolbar>
      </AppBar>

      {description && (
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      )}

      <Grid container spacing={2} sx={{ pt: 2 }}>
        <Grid item xs={12} md={7}>
          <Paper
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f0f0f0",
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Sessions in list ({workingList.length})
              </Typography>
              {isDirty && (
                <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                  Unsaved changes
                </Typography>
              )}
            </Box>

            {isLoading && !items ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <SortableSessionList items={workingList} onReorder={handleReorder} onRemove={handleRemove} />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Add a session
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Search for an active session by name. Sessions already in the list are hidden.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Only standalone category / subcategory sessions can be added — Challenge,
              Series, Audiobook, and Quick Tap sessions are excluded.
            </Typography>
            <SessionAddPicker excludeIds={excludeIds} onAdd={handleAdd} />
          </Paper>
        </Grid>
      </Grid>
    </MainContentContainer>
  );
};

export default EngagementSessionListPage;
